/**
 * Claims List Page - Clara Vale V2
 *
 * Compact layout: ListPageHeader (title + subtitle + time filter)
 * + single-row toolbar with dynamic status pills from workflow engine,
 *   saved views, search, columns, export.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/admin/components/app-shell";
import {
  useClaims,
  useClaimStatusCounts,
  useClaimsTotalCount,
  useMyAssignedClaimsBadge,
  invalidateClaimsQueries,
  useClaimLabelOptions,
  type ClaimRow,
} from "@/admin/hooks/use-claims";
import { LabelChip } from "@/admin/pages/claims/claim-labels-control";
import { ClaimsLabelFilter } from "@/admin/pages/claims/claims-label-filter";
import { useTableState } from "@/admin/hooks/use-table-state";
import { useTableColumnPreferences } from "@/admin/hooks/use-table-column-preferences";
import { useTableFilterPreferences } from "@/admin/hooks/use-table-filter-preferences";
import { useDismissedStatusPills } from "@/admin/hooks/use-dismissed-status-pills";
import { useEntityAttribute } from "@/admin/hooks/use-entity-registration";
import { useExternalRoles } from "@/admin/hooks/use-external-roles";
import {
  DataTable,
  type DataTableColumn,
  type ActiveFilter,
} from "@/admin/components/data-table";
import {
  useTableViews,
  useCreateTableView,
  useDeleteTableView,
  useUpdateTableView,
  useSetOrgDefaultView,
} from "@/admin/hooks/use-table-views";
import { ListPageToolbar } from "@/admin/components/list-page-toolbar";
import { ListPageHeader } from "@/admin/components/shared/list-page-header";
import { BulkImportDialog } from "@/admin/components/imports/bulk-import-dialog";
import { EmptyState } from "@/admin/components/shared/empty-state";
import { UmbrellaChildrenList } from "@/admin/components/claims/umbrella-children-list";
import { formatClaimId } from "@/admin/lib/formatting";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Search,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { VoiceAssistant } from "@/shared/components/voice/voice-assistant";
import { TestVADialog } from "@/admin/components/claims/test-va-dialog";
import { FLAGS, useFeatureFlag } from "@/admin/hooks/use-feature-flag";
import { cn } from "@/shared/lib/utils";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { NotificationDot } from "@/shared/components/notifications/notification-dot";
import { useTableExport } from "@/admin/hooks/use-table-export";
import type { TableViewConfig, TableView } from "@/admin/types/table-views";
import type { StatusPillItem } from "@/admin/components/shared/status-pills";
import type { TimeRange } from "@/admin/components/shared/time-filter";
import { timeRangeToParams } from "@/admin/lib/time-range-params";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useFormatters, useFormattingContext } from "@/shared/lib/formatting";
import {
  defineField,
  formatDisplayValue,
  normaliseClaimField,
  type FormFieldMeta,
} from "@/shared/lib/forms";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { useClaimSchemas } from "@/admin/hooks/use-claim-schemas";
import { selectableLabelOptions } from "@/admin/lib/claim-label-options";

const TABLE_ID = "claims";

/**
 * Columns whose content the backend searches only while they are on screen,
 * sent as `search_columns` - see `ColumnScopedSearchFilter`. Keys must match
 * `column_search_fields` on `ClaimListView`, values the column's own
 * `defaultVisible`.
 *
 * Email and phone are deliberately absent: they are identifiers people paste
 * to find a record, so the backend always searches them whatever is visible.
 */
const SEARCHABLE_COLUMNS: Record<string, boolean> = {
  consumer: true,
  product: true,
  dealer: false,
  assignee: true,
  claim_type: true,
};

/**
 * Assignee(s) for an umbrella parent - the distinct reviewers across its child
 * claims. Rendered as full name(s) to match single-claim rows: one name when
 * the children share a reviewer, otherwise "First Name +N" with the full list
 * in the tooltip so the cell stays compact without falling back to initials.
 */
function AssigneeGroup({
  people,
}: {
  people: { uuid: string; name: string }[];
}) {
  const title = people.map((p) => p.name).join(", ");
  const label =
    people.length <= 1
      ? (people[0]?.name ?? "-")
      : `${people[0].name} +${people.length - 1}`;
  return (
    <span
      className="block truncate text-[13px] text-[var(--ink)]"
      title={title}
    >
      {label}
    </span>
  );
}

export function ClaimsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  // Voice intake affordances ("Talk to us" + "Test VA") only render for
  // tenants with the voice capability enabled. Off by default for everyone
  // else (e.g. Pool Systems), so neither button leaks across tenants.
  const voiceEnabled = useFeatureFlag(FLAGS.VOICE);
  const bulkImportsEnabled = useFeatureFlag(FLAGS.BULK_IMPORTS);
  // Umbrella/multi-item claims add the expand caret to parent rows. Only when
  // that capability is on do we reserve the caret slot on every row (so parent
  // and leaf Claim IDs align); tenants without it keep the un-indented ID.
  const multiItemClaimsEnabled = useFeatureFlag(FLAGS.MULTI_ITEM_CLAIMS);
  const [bulkClaimsOpen, setBulkClaimsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null);
  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  // Record-metadata filter fly-out selection, persisted per tenant+table.
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID);
  const { dismissedKeys: dismissedStatusKeys, dismiss: dismissStatusPill } =
    useDismissedStatusPills(TABLE_ID);
  const [timeRange, setTimeRange] = useState<TimeRange>("all_time");
  const [customRange, setCustomRange] = useState<
    { from: string; to: string } | undefined
  >();
  const [searchParams, setSearchParams] = useSearchParams();
  const myClaimsOnly = searchParams.get("view") === "mine";
  const setMyClaimsOnly = useCallback(
    (v: boolean) => {
      if (v) {
        setSearchParams({ view: "mine" });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const {
    page,
    pageSize,
    sorting,
    setPage,
    setPageSize,
    setSorting,
    queryParams,
  } = useTableState(TABLE_ID);

  // The Type column only makes sense when the tenant's claim entity defines a
  // `claim_type` attribute (i.e. claims can be of more than one type). Tenants
  // without it (e.g. Supagard) have a single implicit claim type, so showing
  // "Basic Claims" everywhere is noise - hide the column entirely.
  // `!!` keeps this a stable boolean - useEntityAttribute may return a fresh
  // object reference each render, which would otherwise rebuild the memoized
  // columns array (deps below) on every render.
  const hasClaimType = !!useEntityAttribute("claim", "claim_type");

  // Triage label options drive the Labels column filter (filter by
  // claims tagged Urgent / Follow Up / etc). Empty for tenants with no
  // `labels` attribute - the column then renders nothing notable.
  const { data: labelOptions = [] } = useClaimLabelOptions();
  // Gate the Labels column + filter on whether the tenant has registered a
  // `labels` attribute on the claim entity, not on whether options happen to
  // be seeded - entity presence is the source of truth (see IfEntityAttribute).
  const hasLabels = !!useEntityAttribute("claim", "labels");
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  // Umbrella nesting: which parent rows are expanded to reveal their children.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((uuid: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const qc = useQueryClient();
  const { data: myClaimsTabData } = useMyAssignedClaimsBadge(true);
  const hasAssignedClaims = (myClaimsTabData?.count ?? 0) > 0;

  // Views
  const { data: views = [] } = useTableViews(TABLE_ID);
  const createView = useCreateTableView();
  const deleteView = useDeleteTableView();
  const updateView = useUpdateTableView();
  const setOrgDefault = useSetOrgDefaultView();

  // Debounced for the query keys; the input keeps raw `search` so typing stays
  // instant. Keyed on the raw value each character fired BOTH queries, and the
  // pill-count one pulls page_size=250 full rows - so a five-letter search sent
  // ten requests, half of them the heaviest call on the page.
  const debouncedSearch = useDebouncedValue(search);

  // Columns on screen, so a hidden one is not searched. Shared by the rows
  // query and the count queries - scoping one and not the other would leave
  // the pills counting matches the table cannot show.
  const searchColumns = useMemo(
    () =>
      Object.keys(SEARCHABLE_COLUMNS)
        .filter((id) => {
          // Claim type is only a column on tenants that define the attribute;
          // without it there is nothing on screen to justify the join.
          if (id === "claim_type" && !hasClaimType) return false;
          return columnVisibility[id] ?? SEARCHABLE_COLUMNS[id];
        })
        .join(","),
    [columnVisibility, hasClaimType],
  );

  const params = useMemo(() => {
    const p: Record<string, string> = {
      ...queryParams,
      ...timeRangeToParams(timeRange, customRange),
    };
    if (debouncedSearch) {
      p.search = debouncedSearch;
      p.search_columns = searchColumns;
    }
    if (stateFilter) p.state = stateFilter;
    if (myClaimsOnly) p.assigned_to = "me";
    if (selectedLabels.length > 0) p.label = selectedLabels.join(",");
    activeFilters.forEach((f) => {
      p[f.field] = f.value;
    });
    // Filter fly-out selections: each facet key (source / claim_schema_uuid /
    // attr__<code>) with its values comma-joined so the backend ORs them.
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(",");
    }
    // Nest child claims under their parent while browsing; when searching or
    // filtering, show every claim flat so results stay complete.
    // My Claims (assigned_to=me) still nests: the backend widens the
    // assigned-to match so a top-level parent is returned when the parent OR
    // any of its child items is assigned to me, so nothing I'm assigned to is
    // hidden and multi-item parents stay collapsible here too (parity with the
    // main list). Genuine search/filter selections still flatten.
    const isFiltering =
      // Debounced, like the term itself: on the raw value the first keystroke
      // dropped `top_level` while `p.search` still held the previous term, so
      // the query shape changed a beat before the search it belongs to.
      !!debouncedSearch ||
      !!stateFilter ||
      selectedLabels.length > 0 ||
      activeFilters.length > 0 ||
      Object.values(filterValues).some((v) => v.length > 0);
    if (!isFiltering) p.top_level = "true";
    return p;
  }, [
    queryParams,
    debouncedSearch,
    stateFilter,
    selectedLabels,
    activeFilters,
    filterValues,
    timeRange,
    customRange,
    myClaimsOnly,
    searchColumns,
  ]);

  const {
    data: claimsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useClaims(params);

  // Params for the pill counts + the unfiltered total. Every narrowing the
  // operator applied is included, so the pills agree with the rows beneath
  // them. `state` is the ONE deliberate omission - it is the pill selection
  // itself, and feeding it back would collapse the bar to the single state
  // already chosen (same rule as the registrations page).
  //
  // Does NOT spread `queryParams`: its `page` made this request
  // `page=N&page_size=250`, which 404s for any N>1 (that page needs 250*N
  // claims), so the pill bar vanished as soon as an operator paginated. Its
  // `ordering` cannot change a count, and refetched all 250 rows on re-sort.
  const unfilteredParams = useMemo(() => {
    const p: Record<string, string> = {
      ...timeRangeToParams(timeRange, customRange),
    };
    if (debouncedSearch) {
      p.search = debouncedSearch;
      p.search_columns = searchColumns;
    }
    if (myClaimsOnly) p.assigned_to = "me";
    if (selectedLabels.length > 0) p.label = selectedLabels.join(",");
    activeFilters.forEach((f) => {
      p[f.field] = f.value;
    });
    // Filter fly-out selections (source / claim_schema_uuid / attr__<code>),
    // comma-joined so the backend ORs them - same shape as `params` above.
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(",");
    }
    // Only `count` is read off this response (header + table pagination), so
    // ask for one row, not 250. The pills come from the aggregate endpoint.
    p.page_size = "1";
    return p;
  }, [
    debouncedSearch,
    selectedLabels,
    activeFilters,
    filterValues,
    timeRange,
    customRange,
    myClaimsOnly,
    searchColumns,
  ]);

  const { data: allClaimsData } = useClaimsTotalCount(unfilteredParams);
  const { data: statusCounts } = useClaimStatusCounts(unfilteredParams);

  useEffect(() => {
    if (!hasAssignedClaims && myClaimsOnly) {
      setMyClaimsOnly(false);
    }
  }, [hasAssignedClaims, myClaimsOnly, setMyClaimsOnly]);

  // Tour-driven: navigate to first claim for workflow walkthrough
  const tourStep = searchParams.get("tourStep");
  useEffect(() => {
    if (tourStep === "workflow" && claimsData?.results?.[0]) {
      const firstClaim = claimsData.results[0] as { uuid: string };
      navigate(`/claims/${firstClaim.uuid}`, { replace: true });
    }
  }, [tourStep, claimsData, navigate]);

  const claims = useMemo(() => claimsData?.results ?? [], [claimsData]);
  const totalCount = allClaimsData?.count ?? claimsData?.count ?? 0;

  // Status pills across ALL states, counted server-side over the whole filtered
  // set. `key` must be the LABEL, not the bucket's state key: selecting a pill
  // sends `?state=`, which the list matches with `current_state__label__iexact`.
  const statusPills: StatusPillItem[] = useMemo(
    () =>
      (statusCounts ?? []).map((b) => ({
        key: b.label,
        label: b.label,
        count: b.count,
      })),
    [statusCounts],
  );

  // Find a highlight stat for the subtitle
  const highlightStat = useMemo(() => {
    const underReview = statusPills.find(
      (p) =>
        p.label.toLowerCase().includes("review") ||
        p.label.toLowerCase().includes("submitted"),
    );
    return underReview
      ? { label: "requiring action", value: underReview.count }
      : undefined;
  }, [statusPills]);

  const handleTimeFilterChange = useCallback(
    (range: TimeRange, custom?: { from: string; to: string }) => {
      setTimeRange(range);
      setCustomRange(custom);
      setPage(1);
    },
    [setPage],
  );

  // The tenant's own word for a partner, so the column is not a hardcoded
  // "Dealer". Same source as the registrations table.
  const { data: externalRoles = [] } = useExternalRoles();
  const partnerRoleName = externalRoles[0]?.name ?? "Dealer";

  // Field types + labels for the attribute columns, so money, dates and options
  // render formatted. Roles that cannot read schemas fall back to the raw key.
  const { can } = usePermissions();
  const { data: claimSchemas } = useClaimSchemas(can("claim-schemas.view"));
  const { locale, currency } = useFormattingContext();
  const attributeFields = useMemo(() => {
    const fields = new Map<string, FormFieldMeta>();
    for (const schema of claimSchemas ?? []) {
      for (const attr of schema.attributes) {
        if (!fields.has(attr.code_name)) {
          fields.set(attr.code_name, normaliseClaimField(attr));
        }
      }
    }
    return fields;
  }, [claimSchemas]);

  // Build dynamic attribute columns from claim schema fields
  const attributeColumns: DataTableColumn<ClaimRow>[] = useMemo(() => {
    const seen = new Set<string>();
    const cols: DataTableColumn<ClaimRow>[] = [];
    // Collect the union of all attribute keys across loaded claims
    for (const claim of claims) {
      const attrs = claim.attribute_values as
        Record<string, unknown> | undefined;
      if (!attrs) continue;
      for (const key of Object.keys(attrs)) {
        if (seen.has(key)) continue;
        seen.add(key);
        // Unknown to the schema: humanise the key, "issue_type" -> "Issue Type"
        const field =
          attributeFields.get(key) ??
          defineField({
            code_name: key,
            display_name: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()),
            field_type: "TEXT",
          });
        cols.push({
          id: `attr:${key}`,
          header: field.display_name,
          defaultVisible: false,
          cell: (row) => (
            <span className="block max-w-[200px] truncate text-[12px] text-[var(--ink-2)]">
              {formatDisplayValue(
                field,
                (row.attribute_values as Record<string, unknown>)?.[key],
                { locale, currency },
              )}
            </span>
          ),
        });
      }
    }
    return cols;
  }, [claims, attributeFields, locale, currency]);

  const baseColumns: DataTableColumn<ClaimRow>[] = useMemo(
    () => [
      {
        id: "uuid",
        header: "Claim ID",
        sortable: false,
        cell: (row) => (
          <span className="flex items-center gap-2">
            {/* Fixed-width caret slot, only when multi-item claims are enabled
                (the only case a parent row shows an expand control): parents get
                the control, leaf claims get an equal-width spacer, so all Claim
                IDs align in one column. Tenants without multi-item claims render
                no slot and keep the un-indented ID. */}
            {multiItemClaimsEnabled && (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                {(row.child_claim_count ?? 0) > 0 ? (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={
                      expandedRows.has(row.uuid)
                        ? "Collapse items"
                        : "Expand items"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(row.uuid);
                    }}
                  >
                    {expandedRows.has(row.uuid) ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </Button>
                ) : null}
              </span>
            )}
            {row.is_flagged && (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-[var(--status-warn)]"
                title={row.flag_reason || "Flagged"}
              />
            )}
            {row.spotlight?.active && (
              <NotificationDot
                level={row.spotlight.level}
                title={row.spotlight.title || "Needs attention"}
              />
            )}
            <span
              className={cn(
                "truncate font-mono text-[12px] uppercase",
                // New-claim emphasis: darken + bolden the Claim ID for a
                // freshly-submitted, not-yet-actioned claim (spotlight.is_new).
                // Clears when the "awaiting review" notification is read or the
                // claim leaves its initial state. Distinct from the attention
                // dot (any unread), so unread messages/assignments don't bold.
                row.spotlight?.is_new
                  ? "font-semibold text-[var(--ink)]"
                  : "text-[var(--ink-3)]",
              )}
            >
              {row.claim_number || formatClaimId(row.uuid)}
            </span>
          </span>
        ),
      },
      ...(hasClaimType
        ? [
            {
              id: "claim_type",
              header: "Type",
              defaultVisible: SEARCHABLE_COLUMNS.claim_type,
              sortable: false,
              cell: (row: ClaimRow) => {
                // Prefer claim_schema_key (clean: "installed", "job_stop")
                // over claim_schema_name (verbose: "OC Installed Claims").
                const key = row.claim_schema_key;
                const name = row.claim_schema_name;
                if (!key && !name)
                  return <span className="text-[var(--ink-3)]">{"-"}</span>;
                const label = key
                  ? key.replace(/_/g, " ")
                  : name
                      .replace(/\s+Claims?$/i, "")
                      .replace(/_/g, " ")
                      .trim();
                return (
                  <span className="block truncate text-[12px] font-medium capitalize text-[var(--ink-2)]">
                    {label}
                  </span>
                );
              },
            } satisfies DataTableColumn<ClaimRow>,
          ]
        : []),
      ...(hasLabels
        ? [
            {
              id: "labels",
              header: "Labels",
              sortable: false,
              cell: (row: ClaimRow) => {
                const vals = Array.isArray(row.attribute_values?.labels)
                  ? (row.attribute_values.labels as string[])
                  : [];
                if (vals.length === 0)
                  return <span className="text-[var(--ink-3)]">{"-"}</span>;
                return (
                  <span className="flex flex-wrap gap-1">
                    {vals.map((v) => {
                      const opt = labelOptions.find((o) => o.value === v);
                      return (
                        <LabelChip
                          key={v}
                          label={opt?.label ?? v}
                          color={opt?.color}
                        />
                      );
                    })}
                  </span>
                );
              },
            } satisfies DataTableColumn<ClaimRow>,
          ]
        : []),
      {
        id: "consumer",
        header: "Customer",
        defaultVisible: SEARCHABLE_COLUMNS.consumer,
        sortable: true,
        sortField: "holding__consumer_account__first_name",
        meta: { filterType: "text" as const, filterField: "search" },
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink)]">
            {row.consumer_account_name || row.consumer_account_email || "-"}
          </span>
        ),
      },
      {
        id: "consumer_email",
        header: "Email",
        defaultVisible: false,
        sortable: true,
        sortField: "holding__consumer_account__email",
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink-2)]">
            {row.consumer_account_email || "-"}
          </span>
        ),
      },
      {
        id: "consumer_phone",
        header: "Phone",
        defaultVisible: false,
        sortable: false,
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink-2)]">
            {row.consumer_account_phone || "-"}
          </span>
        ),
      },
      {
        id: "consumer_address",
        header: "Address",
        defaultVisible: false,
        sortable: false,
        cell: (row) => (
          <span
            className="block truncate text-[13px] text-[var(--ink-2)]"
            title={row.consumer_account_address || undefined}
          >
            {row.consumer_account_address || "-"}
          </span>
        ),
      },
      {
        id: "product",
        header: "Product",
        defaultVisible: SEARCHABLE_COLUMNS.product,
        sortable: true,
        sortField: "holding__product__cached_long_name",
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink-2)]">
            {row.product_name || "-"}
          </span>
        ),
      },
      {
        id: "dealer",
        header: partnerRoleName,
        defaultVisible: SEARCHABLE_COLUMNS.dealer,
        sortable: true,
        sortField: "holding__partnership__downstream_organisation__name",
        cell: (row) => (
          <span
            className="block truncate text-[13px] text-[var(--ink-2)]"
            title={row.dealer_name || undefined}
          >
            {row.dealer_name || "-"}
          </span>
        ),
      },
      {
        id: "state",
        header: "State",
        sortable: false,
        cell: (row) => {
          if (!row.claim_state)
            return <span className="text-[var(--ink-3)]">{"-"}</span>;
          return (
            <div className="flex flex-col items-start gap-1">
              <StatusBadge
                status={row.claim_state}
                color={row.claim_state_color}
              />
              {/* Requesting an inspection deliberately does not move the
                  claim - the lab keeps working while it runs - so the state
                  alone cannot say the claim is waiting on someone to visit.
                  A second line rather than a reworded pill: the state IS Lab
                  Analysis, and renaming it would misreport the workflow and
                  break the filters counted off it. */}
              {/* An escalation PAUSES the claim - every action on it is
                  refused until someone resolves it - so it earns a warning
                  colour rather than the stage's, which is for where the
                  claim is, not for something being wrong with it. */}
              {row.has_open_escalation && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] bg-[var(--status-error)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-white">
                  <TriangleAlert className="h-2.5 w-2.5" />
                  {t("claims.list.escalated", "Escalated")}
                </span>
              )}
              {row.has_open_inspection && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-white"
                  // Takes the STATE's own colour so the marker reads as
                  // belonging to the stage above it rather than as a second,
                  // competing status. The value is per-row API data, which is
                  // the same reason StatusBadge sets its API colour inline.
                  style={{
                    backgroundColor: row.claim_state_color ?? "var(--accent)",
                  }}
                >
                  <Search className="h-2.5 w-2.5" />
                  {t("claims.list.inspectionRequested", "Inspection requested")}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "assignee",
        header: "Assignee",
        defaultVisible: SEARCHABLE_COLUMNS.assignee,
        sortable: false,
        cell: (row) => {
          // Umbrella parents have no assignee of their own; show the distinct
          // reviewers across their child claims (grouped when they differ).
          const group = row.child_assignees ?? [];
          if ((row.child_claim_count ?? 0) > 0 && group.length > 0) {
            return <AssigneeGroup people={group} />;
          }
          // WPS-160: show the person who owns the claim's CURRENT stage (Lab
          // Analysis -> Lab holder, the settlement stages -> Settlement holder,
          // inspection stages -> Inspector, legal-review stages -> "External",
          // every other stage -> the Resolution Specialist). Resolved
          // server-side and falling back to the primary owner, so a claim in
          // Lab Analysis no longer shows a blank/RS while the Lab rep works it.
          const assignee = row.current_stage_assignee ?? row.assigned_to;
          if (!assignee)
            return <span className="text-[11px] text-[var(--ink-3)]">-</span>;
          return (
            <span
              className="block truncate text-[13px] text-[var(--ink)]"
              title={assignee.name}
            >
              {assignee.name}
            </span>
          );
        },
      },
      {
        id: "created_at",
        header: "Created",
        sortable: true,
        sortField: "created_at",
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      ...attributeColumns,
    ],
    [
      t,
      attributeColumns,
      hasClaimType,
      hasLabels,
      labelOptions,
      expandedRows,
      toggleExpanded,
      formatDate,
      multiItemClaimsEnabled,
      partnerRoleName,
    ],
  );

  // Gate column sortability on the BE-declared sortable_columns so only
  // genuinely orderable columns show a sort control (some columns were
  // declared sortable client-side but silently dropped server-side). Falls
  // back to the static flags until the metadata arrives.
  const sortableFields = useMemo(
    () => new Set((claimsData?.sortable_columns ?? []).map((c) => c.field)),
    [claimsData?.sortable_columns],
  );
  const columns = useMemo(
    () =>
      sortableFields.size === 0
        ? baseColumns
        : baseColumns.map((col) =>
            col.sortField
              ? { ...col, sortable: sortableFields.has(col.sortField) }
              : col,
          ),
    [baseColumns, sortableFields],
  );

  function buildCurrentConfig(
    overrides: {
      columnVisibility?: Record<string, boolean>;
      columnOrder?: string[];
    } = {},
  ): TableViewConfig {
    const nextColumnVisibility = overrides.columnVisibility ?? columnVisibility;
    const nextColumnOrder = overrides.columnOrder ?? columnOrder;
    const visibleIds = columns
      .filter((col) => {
        if (col.id in nextColumnVisibility)
          return nextColumnVisibility[col.id] !== false;
        return col.defaultVisible !== false;
      })
      .map((col) => col.id);
    return {
      columns: visibleIds,
      columnOrder: nextColumnOrder,
      sorting: sorting ?? null,
      filters: activeFilters.map((f) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
    };
  }

  const currentConfig = buildCurrentConfig();

  const { exportCsv, isExporting } = useTableExport({
    endpoint: "/claims/claims/",
    columns,
    columnVisibility,
    params,
  });

  const handleSelectView = useCallback(
    (view: TableView | null) => {
      setActiveViewUuid(view?.uuid ?? null);
      if (view?.config) {
        if (view.config.columns) {
          const vis: Record<string, boolean> = {};
          columns.forEach((c) => {
            vis[c.id] = view.config.columns!.includes(c.id);
          });
          setColumnVisibility(vis);
        } else {
          setColumnVisibility({});
        }
        setColumnOrder(view.config.columnOrder ?? []);
        if (view.config.sorting) {
          setSorting(view.config.sorting);
        } else {
          setSorting(null);
        }
        if (view.config.filters?.length) {
          setActiveFilters(
            view.config.filters.map((f) => ({
              ...f,
              label: `${columns.find((c) => c.meta?.filterField === f.field)?.header ?? f.field}: ${f.value}`,
            })),
          );
        } else {
          setActiveFilters([]);
        }
      } else {
        applyDefaultColumnPreferences();
        setSorting(null);
        setActiveFilters([]);
      }
    },
    [
      applyDefaultColumnPreferences,
      columns,
      setColumnOrder,
      setSorting,
      setColumnVisibility,
    ],
  );

  return (
    <AppShell>
      <main className="page-container pb-10 pt-10">
        <div>
          {/* Voice intake affordances, gated behind the `voice` capability
            flag so only voice-enabled tenants see them. The "Talk to us"
            widget opens the in-platform live-voice assistant; "Test VA"
            fires a claim through the same intake pipeline the Voice Agent
            uses, via the admin-authenticated /api/claims/test-va-intake/
            endpoint (no API key in the frontend). */}
          {voiceEnabled && (
            <div className="mb-3 flex justify-end gap-2">
              <VoiceAssistant
                label="Talk to us"
                className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]"
              />
              <TestVADialog />
            </div>
          )}
          <div data-tour="claims-list">
            <ListPageHeader
              title="Claims"
              configHelp={{
                sections: ["claim.detail_fields"],
                returnLabel: "Claims",
              }}
              totalCount={totalCount}
              highlightStat={highlightStat}
              timeFilterValue={timeRange}
              timeFilterCustomRange={customRange}
              onTimeFilterChange={handleTimeFilterChange}
              actionButton={{
                label: "Submit Claim",
                onClick: () => navigate("/claims/submit"),
              }}
              secondaryActionButton={
                bulkImportsEnabled
                  ? {
                      label: "Bulk Claims",
                      onClick: () => setBulkClaimsOpen(true),
                    }
                  : undefined
              }
            />

            {/* All Claims / My Claims tabs */}
            <div className="mb-4 flex items-center gap-1 border-b-[0.5px] border-b-[var(--rule)]">
              <Button
                variant="ghost"
                onClick={() => setMyClaimsOnly(false)}
                className={cn(
                  "rounded-none border-b-2 px-3 pb-2 pt-1 text-[11px] uppercase tracking-[0.1em] transition-colors",
                  !myClaimsOnly
                    ? "border-b-[var(--green)] text-[var(--ink)]"
                    : "border-b-transparent text-[var(--ink-3)]",
                )}
              >
                All Claims
              </Button>
              {hasAssignedClaims ? (
                <Button
                  variant="ghost"
                  onClick={() => setMyClaimsOnly(true)}
                  className={cn(
                    "rounded-none border-b-2 px-3 pb-2 pt-1 text-[11px] uppercase tracking-[0.1em] transition-colors",
                    myClaimsOnly
                      ? "border-b-[var(--green)] text-[var(--ink)]"
                      : "border-b-transparent text-[var(--ink-3)]",
                  )}
                >
                  My Claims
                </Button>
              ) : null}
            </div>

            <ListPageToolbar<ClaimRow>
              layout="compact"
              tableId={TABLE_ID}
              search={search}
              onSearchChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
              searchPlaceholder="Search claims..."
              statusPills={statusPills}
              activeStatusKey={stateFilter}
              onStatusSelect={(key) => {
                setStateFilter(key);
                setPage(1);
              }}
              statusTotalCount={totalCount}
              dismissedStatusKeys={dismissedStatusKeys}
              onStatusDismiss={(key) => {
                dismissStatusPill(key);
                if (stateFilter === key) {
                  setStateFilter(null);
                  setPage(1);
                }
              }}
              views={views}
              activeViewUuid={activeViewUuid}
              onSelectView={handleSelectView}
              onSaveView={(name) =>
                createView.mutate({
                  table_id: TABLE_ID,
                  name,
                  config: currentConfig,
                })
              }
              onDeleteView={(uuid) => {
                deleteView.mutate({ uuid, tableId: TABLE_ID });
                if (activeViewUuid === uuid) setActiveViewUuid(null);
              }}
              onUpdateView={(uuid) =>
                updateView.mutate({
                  uuid,
                  tableId: TABLE_ID,
                  config: currentConfig,
                })
              }
              onRenameView={(uuid, name) =>
                updateView.mutate({ uuid, tableId: TABLE_ID, name })
              }
              onSetOrgDefault={() =>
                setOrgDefault.mutate({
                  table_id: TABLE_ID,
                  name: "Default",
                  config: currentConfig,
                })
              }
              currentConfig={currentConfig}
              columns={columns}
              activeFilters={activeFilters}
              onFiltersChange={(filters) => {
                setActiveFilters(filters);
                setPage(1);
              }}
              filterFacets={claimsData?.filter_options ?? []}
              filterValues={filterValues}
              onFilterValuesChange={(values) => {
                setFilterValues(values);
                setPage(1);
              }}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={(visibility) => {
                setColumnVisibility(visibility);
                if (activeViewUuid) {
                  updateView.mutate({
                    uuid: activeViewUuid,
                    tableId: TABLE_ID,
                    config: buildCurrentConfig({
                      columnVisibility: visibility,
                    }),
                  });
                } else {
                  saveColumnVisibility(visibility);
                }
              }}
              alwaysShownColumns={["uuid", "product", "state"]}
              columnOrder={columnOrder}
              onColumnOrderChange={(order) => {
                setColumnOrder(order);
                if (activeViewUuid) {
                  updateView.mutate({
                    uuid: activeViewUuid,
                    tableId: TABLE_ID,
                    config: buildCurrentConfig({ columnOrder: order }),
                  });
                } else {
                  saveColumnOrder(order);
                }
              }}
              onExport={exportCsv}
              isExporting={isExporting}
              trailingControls={
                hasLabels ? (
                  <ClaimsLabelFilter
                    options={selectableLabelOptions(labelOptions)}
                    selected={selectedLabels}
                    onChange={(next) => {
                      setSelectedLabels(next);
                      setPage(1);
                    }}
                  />
                ) : undefined
              }
            />
          </div>

          <DataTable<ClaimRow>
            tableId={TABLE_ID}
            columns={columns}
            data={claims}
            totalCount={totalCount}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={refetch}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sorting={sorting}
            onSortingChange={setSorting}
            onRowClick={(row) => navigate(`/claims/${row.uuid}`)}
            renderRowDetail={(row) =>
              expandedRows.has(row.uuid) && (row.child_claim_count ?? 0) > 0 ? (
                <UmbrellaChildrenList
                  parentUuid={row.uuid}
                  columns={columns}
                  columnVisibility={columnVisibility}
                  columnOrder={columnOrder}
                />
              ) : null
            }
            renderRowDetailStartColumn={0}
            highlightExpandedDetail
            emptyStateNode={
              <EmptyState
                icon={FileText}
                title="No claims have been submitted"
                description="Claims will appear here once customers or staff submit them."
              />
            }
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
          />
        </div>
      </main>

      <BulkImportDialog
        open={bulkClaimsOpen}
        onOpenChange={setBulkClaimsOpen}
        title="Bulk Claims"
        onComplete={() => invalidateClaimsQueries(qc)}
      />
    </AppShell>
  );
}
